import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import StyleIcon from '@mui/icons-material/Style';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockIcon from '@mui/icons-material/Lock';
import { useNetwork } from '../../../utils/network';
import { useShowSnackbar } from '../../../utils/snackbar';
import { reserveKycTab, openKycForm, releaseKycTab } from './openKycForm';

type KycStatus = 'not_started' | 'pending' | 'approved' | 'declined';

/** Server-supplied explanation for the most recent decline. */
type DeclineReason = { codes: string[]; message: string; at: string };
/* `value` is the package's own price in AUD, sent by the server. Deliberately not a constant in
   this file: the page makes a dollar claim, and a hardcoded one would go on advertising $40
   after someone reprices the pack. Null simply drops the claim rather than showing a stale one. */
type Reward = { offered: boolean; granted: boolean; pending: boolean; value?: number | null };

// "$40", not "$40.00" — trailing zeroes read like a receipt, not an offer
const aud = (n: number) =>
  `$${Number.isInteger(n) ? n : n.toFixed(2)} AUD`;

const BRAND = '#38e8ff';
const GREEN = '#42e6a4';
const GOLD = '#ffcf40';
const POLL_MS = 5000;

const STATUS_META: Record<KycStatus, { label: string; color: string }> = {
  approved: { label: 'Verified', color: GREEN },
  pending: { label: 'In review', color: '#ff9800' },
  declined: { label: 'Declined', color: '#f44336' },
  not_started: { label: 'Not started', color: '#9e9e9e' }
};

const CARD_SX = {
  border: '1px solid rgba(255,255,255,0.13)',
  borderRadius: '16px',
  backgroundColor: 'rgba(0,0,0,0.28)'
};

/* What the member is actually being offered. Kept generic about counts — the pack's contents
   come from the package's rarity config, which is data, not code, and must not be re-stated
   here where it would quietly go stale. */
const INCLUDES = [
  { icon: <StyleIcon />, title: 'A full starter deck', body: 'Enough cards to play the TCG straight away — built and saved for you automatically.' },
  { icon: <AutoAwesomeIcon />, title: 'Up to Relic rarity', body: 'The top starter tier. Every card is a real collectible you own and can trade.' },
  { icon: <Inventory2Icon />, title: 'Yours to keep', body: 'No subscription, no purchase, no catch. Verify once and the pack is yours.' }
];

const STEPS = [
  'Tap the button below — we hand you to our verification partner.',
  'Photograph your ID and take a quick selfie. About two minutes.',
  'Your pack lands on your dashboard, ready to open.'
];

/**
 * Identity Verification (KYC) — the canonical place to verify.
 *
 * The page leads with the free starter pack rather than the compliance step, because that is
 * the reason a member is here. The verification flow underneath is unchanged.
 */
export const KycPage = () => {
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const navigate = useNavigate();
  const [status, setStatus] = useState<KycStatus | null>(null);
  const [reward, setReward] = useState<Reward>({ offered: false, granted: false, pending: false, value: null });
  // the starter-pack lock: this member already HAS the pack (granted at sign-up); verifying unlocks it
  const [locked, setLocked] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);
  /* Why the last attempt failed, phrased for a member. Server-side only — the
     page never interprets provider reason codes itself. */
  const [declineReason, setDeclineReason] = useState<DeclineReason | null>(null);
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async (): Promise<KycStatus | null> => {
    try {
      const data = await get('/kyc/status', true);
      setStatus(data.status);
      setConfigured(data.configured !== false);
      if (data.reward) setReward(data.reward);
      setLocked(!!data.starterPack?.locked);
      setDeclineReason(data.declineReason || null);
      return data.status as KycStatus;
    } catch {
      setConfigured(false);
      return null;
    }
  }, [get]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startVerification = async () => {
    /* Reserved HERE, synchronously, before the await below. A popup is only allowed from the
       live call stack of a user gesture, so opening it after the request returns is silently
       blocked on every mobile browser — see openKycForm. */
    const tab = reserveKycTab();
    setStarting(true);
    try {
      const data = await post('/kyc/start', {}, true);
      if (data.alreadyVerified || data.status === 'approved') {
        releaseKycTab(tab);
        setStatus('approved');
        fetchStatus(); // pick up the pack that approval just granted
        return;
      }
      if (data.formUrl) {
        const where = openKycForm(tab, data.formUrl);
        setStatus('pending');
        // If the form took over THIS tab there is nothing left to poll — this page is on its
        // way out, and fetchStatus on mount picks the result up when KYCAID redirects back.
        if (where === 'self') return;
        setPolling(true);
        pollRef.current = setInterval(async () => {
          const s = await fetchStatus();
          if (s === 'approved') {
            stopPolling();
            showSnackbar('Identity verified — thank you!', 'success');
          } else if (s === 'declined') {
            stopPolling();
          }
        }, POLL_MS);
        return;
      }
      releaseKycTab(tab); // no URL came back — don't leave a blank tab sitting there
    } catch (error: any) {
      releaseKycTab(tab);
      showSnackbar(error.message || 'Could not start verification.', 'error');
    } finally {
      setStarting(false);
    }
  };

  const meta = status ? STATUS_META[status] : null;
  const isApproved = status === 'approved';
  const offering = reward.offered && !isApproved;

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', p: { xs: 2, md: 4 } }}>
      {/* ---- the offer, first ---- */}
      <Stack alignItems="center" spacing={1.25} sx={{ mb: 3, textAlign: 'center' }}>
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            border: `1px solid ${isApproved ? GREEN : BRAND}44`,
            background: `radial-gradient(circle at 30% 25%, ${isApproved ? GREEN : BRAND}2e, transparent 70%)`
          }}
        >
          {offering ? (
            <Inventory2Icon sx={{ fontSize: 34, color: GOLD }} />
          ) : (
            <VerifiedUserIcon sx={{ fontSize: 34, color: isApproved ? GREEN : BRAND }} />
          )}
        </Box>

        {offering ? (
          <>
            <Typography
              variant="overline"
              sx={{ color: GOLD, letterSpacing: '0.34em', fontWeight: 800, lineHeight: 1 }}
            >
              {locked ? 'Unlock your free pack' : 'Free for verified members'}
            </Typography>
            <Typography variant="h3" fontWeight={900} sx={{ textWrap: 'balance' }}>
              {locked ? 'Unlock your Relic Starter Pack' : 'Claim your free Relic Starter Pack'}
            </Typography>

            {/* The price the pack actually sells for, struck through beside FREE — the whole
                point of the page is that they are getting something with a real number on it. */}
            {reward.value != null && (
              <Stack direction="row" spacing={1.25} alignItems="baseline" sx={{ pt: 0.25 }}>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'line-through',
                    textDecorationColor: 'rgba(255,255,255,0.45)',
                    fontSize: 20,
                    fontWeight: 600
                  }}
                >
                  {aud(reward.value)}
                </Typography>
                <Typography sx={{ color: GOLD, fontSize: 26, fontWeight: 900, lineHeight: 1 }}>
                  FREE
                </Typography>
              </Stack>
            )}

            <Typography sx={{ color: 'text.secondary', maxWidth: 520 }}>
              {locked
                ? 'Your Relic Starter Pack is already in your collection. Verify your identity once to unlock trading, withdrawals and ranked play — or they unlock on their own once you have spent A$99 on INCRYPT.'
                : reward.value != null
                  ? `Verify your identity once and we'll drop a ${aud(reward.value)} Relic Starter Pack into your collection — no purchase, no subscription.`
                  : "Verify your identity once and we'll drop a complete starter deck into your collection — no purchase, no subscription."}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h3" fontWeight={900} sx={{ textWrap: 'balance' }}>
              Identity Verification
            </Typography>
            <Typography sx={{ color: 'text.secondary', maxWidth: 520 }}>
              A quick identity check (KYC) unlocks member promotions. You can keep using the site
              as normal in the meantime.
            </Typography>
          </>
        )}
      </Stack>

      {configured === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: BRAND }} />
        </Box>
      ) : !configured ? (
        <Box sx={{ ...CARD_SX, p: 3, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>
            Identity verification isn&apos;t available just yet — please check back soon.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {/* ---- what's in the pack ---- */}
          {offering && (
            <Box sx={{ ...CARD_SX, p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                {INCLUDES.map((item) => (
                  <Stack key={item.title} direction="row" spacing={1.75} alignItems="flex-start">
                    <Box
                      sx={{
                        flexShrink: 0,
                        mt: '2px',
                        color: GOLD,
                        display: 'grid',
                        placeItems: 'center',
                        '& svg': { fontSize: 22 }
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, lineHeight: 1.4 }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: 14.5 }}>
                        {item.body}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {/* ---- status + action ---- */}
          <Box sx={{ ...CARD_SX, p: { xs: 2, md: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Typography sx={{ color: 'text.secondary' }}>Status:</Typography>
              {meta && (
                <Chip
                  label={meta.label}
                  sx={{ backgroundColor: meta.color, color: '#04222a', fontWeight: 800 }}
                />
              )}
            </Stack>

            {isApproved ? (
              <Stack alignItems="center" spacing={2}>
                <Typography textAlign="center" sx={{ fontWeight: 700 }}>
                  You&apos;re verified — thank you.
                </Typography>
                {reward.pending ? (
                  <>
                    <Typography textAlign="center" sx={{ color: 'text.secondary' }}>
                      Your free Relic Starter Pack is waiting on your dashboard, unopened.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/member-portal')}
                      sx={{ fontWeight: 800, backgroundColor: GREEN, color: '#04222a', px: 4 }}
                    >
                      Open my pack
                    </Button>
                  </>
                ) : (
                  <Typography textAlign="center" sx={{ color: 'text.secondary' }}>
                    {reward.granted
                      ? 'Your free starter pack has been added to your collection.'
                      : "You're all set."}
                  </Typography>
                )}
              </Stack>
            ) : polling || status === 'pending' ? (
              /* A verification is already open, so DON'T offer to start another.
               *
               * `polling` only lives in this page session: reload, or come back through the
               * partner's redirect, and it is false again. Falling through to the start button
               * then told a member who was mid-verification that they had not begun — and every
               * press mints a NEW verification with KYCAID, which is how members ended up with
               * several, only the last of which we had stored (and that one abandoned). Reading
               * the persisted 'pending' status instead makes the in-progress state survive a
               * reload. The restart below is kept deliberately quiet, because someone who
               * genuinely abandoned the form must not be stranded here with no way forward. */
              <Stack alignItems="center" spacing={2} sx={{ py: 1 }}>
                <CircularProgress sx={{ color: BRAND }} />
                <Typography textAlign="center" sx={{ color: 'text.secondary' }}>
                  {polling
                    ? 'Finish the verification in the tab we opened. This page updates automatically once you’re done.'
                    : 'Verification in progress — we’re waiting on our verification partner. This page updates automatically, and you don’t need to do anything else.'}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  disabled={starting}
                  onClick={startVerification}
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  {starting ? 'Starting…' : 'Didn’t get to finish? Start again'}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                {/* ---- how it works ---- */}
                <Stack spacing={1.25}>
                  {STEPS.map((step, i) => (
                    <Stack key={step} direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          border: `1px solid ${BRAND}55`,
                          color: BRAND,
                          font: '800 12px/1 monospace'
                        }}
                      >
                        {i + 1}
                      </Box>
                      <Typography sx={{ color: 'text.secondary', fontSize: 14.5 }}>
                        {step}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                {status === 'declined' && (
                  /* Say WHAT went wrong and what to do about it. The old copy said
                     only "could not be approved", which is why the last declined
                     member went to Discord — and support could not answer either. */
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: '1px solid rgba(244,67,54,0.35)',
                      background: 'rgba(244,67,54,0.06)'
                    }}
                  >
                    <Typography sx={{ color: '#f44336', fontWeight: 800, fontSize: 15 }}>
                      Your last verification could not be approved
                    </Typography>
                    <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 14.5, lineHeight: 1.6 }}>
                      {declineReason?.message ||
                        'We could not verify your identity from what was submitted. You can try again below.'}
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={starting}
                  onClick={startVerification}
                  sx={{
                    fontWeight: 900,
                    fontSize: 16,
                    py: 1.4,
                    backgroundColor: offering ? GOLD : BRAND,
                    color: '#04222a',
                    '&:hover': { backgroundColor: offering ? '#ffdb70' : '#63eeff' }
                  }}
                >
                  {starting
                    ? 'Starting…'
                    : status === 'declined'
                      ? 'Try again'
                      : offering
                        ? 'Verify and claim my pack'
                        : 'Start verification'}
                </Button>
              </Stack>
            )}
          </Box>

          {/* ---- the trust line: keep it, it does real work ---- */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ color: 'text.secondary', px: 1 }}
          >
            <LockIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13.5, textAlign: 'center' }}>
              You&apos;ll be taken to our verification partner to submit your ID. We never see or
              store your documents.
            </Typography>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default KycPage;
